/*
 * Copyright 2015 Kosmos  contact@kosmos.fr
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {GlobalWorkerOptions, getDocument} from 'pdfjs-dist/build/pdf.mjs';
import {EventBus, PDFLinkService, PDFSinglePageViewer, PDFViewer} from 'pdfjs-dist/web/pdf_viewer.mjs';
import {closest} from 'kosmos-js-utils/dist/lib/closest';
import {observeOnDomChange} from 'kosmos-js-utils/dist/lib/observeOnDomChange';
import {parseInteger} from 'kosmos-js-utils/dist/lib/number/parseInteger';

const defaultWorkerSrc = '/content/js/libs/pdf.worker.min.js';
if (typeof window !== 'undefined' && 'Worker' in window) {
    const pdfElement = document.querySelector('[data-pdfurl]');
    const workerUrlBase = pdfElement?.getAttribute('data-pdfworkerurlbase') || '';
    GlobalWorkerOptions.workerSrc = `${workerUrlBase}${defaultWorkerSrc}`;
}

const DEFAULT_SCALE_DELTA = 1.1;
const MIN_SCALE = 0.25;
const MAX_SCALE = 10.0;

/**
 * Permet de calculer par rapport au ratio courant, le prochain ratio après un zoom
 * @param {Number} currentScale le ratio courant
 * @return {Number} le prochain ratio après un zoom (ne peut être plus grand que 10)
 */
function getZoomInScale(currentScale) {
    let newScale = (currentScale * DEFAULT_SCALE_DELTA).toFixed(2);
    newScale = Math.ceil(newScale * 10) / 10;
    newScale = Math.min(MAX_SCALE, newScale);
    return newScale;
}

/**
 * Permet de calculer par rapport au ratio courant, le prochain ratio après un dézoom
 * @param {Number} currentScale le ratio courant
 * @return {Number} le prochain ratio après un zoom (ne peut être plus petit que 0.25)
 */
function getZoomOutScale(currentScale) {
    let newScale = (currentScale / DEFAULT_SCALE_DELTA).toFixed(2);
    newScale = Math.floor(newScale * 10) / 10;
    newScale = Math.max(MIN_SCALE, newScale);
    return newScale;
}

/**
 * Initialise les écouteurs d'événements pour le conteneur lié au visualiseur PDF.
 *
 * @param {HTMLElement} container - Le conteneur principal où les événements sont attachés.
 * @param {Object} viewer - L'objet représentant le visualiseur PDF.
 * @param {Object} eventBus - Le gestionnaire des événements pour le PDF.
 * @param {HTMLInputElement} currentPageField - Le champ de saisie affichant la page actuelle.
 * @return {void} Aucune valeur de retour.
 */
function initContainerListener(container, viewer, eventBus, currentPageField) {
    const rotateclockwise = container.querySelector('.js-rotatec');
    const rotatecounterclockwise = container.querySelector('.js-rotatecc');
    const previousPage = container.querySelector('.js-previousPage');
    const nextPage = container.querySelector('.js-nextPage');
    const resizePdf = container.querySelector('.js-auto');
    const zoomIn = container.querySelector('.js-zoomin');
    const zoomOut = container.querySelector('.js-zoomout');
    const initScale = container.dataset.initscale || '1';

    eventBus.on('pagesinit', function adjustCurrentScale() {
        viewer.currentScaleValue = initScale;

        viewer.currentPageNumber = 1;
        if (currentPageField) {
            currentPageField.value = viewer.currentPageNumber;
        }
    });
    eventBus.on('scalechanging', function adjustScale(event) {
        resizePdf.innerHTML = `<span>${(event.scale * 100).toFixed(0)} %</span>`;
    });
    container.addEventListener('updateviewarea', function updateView(e) {
        e.target.querySelectorAll('.page').forEach((page) => page.classList.add('pdf-viewer__page'));
    });
    zoomIn.addEventListener('click', function zoomInHandler() {
        viewer.currentScale = getZoomInScale(viewer.currentScale);
    });
    zoomOut.addEventListener('click', function zoomOutHandler() {
        viewer.currentScale = getZoomOutScale(viewer.currentScale);
    });
    resizePdf.addEventListener('click', function noZoomHandler() {
        viewer.currentScale = 1;
    });

    if (rotateclockwise) {
        rotateclockwise.addEventListener('click', function rotatec() {
            viewer.pagesRotation += 90;
        });
    }
    if (rotatecounterclockwise) {
        rotatecounterclockwise.addEventListener('click', function rotatecc() {
            viewer.pagesRotation -= 90;
        });
    }
    if (previousPage) {
        previousPage.addEventListener('click', function previous() {
            if (viewer.currentPageNumber > 1) {
                viewer.currentPageNumber--;
                currentPageField.value = viewer.currentPageNumber;
            }
        });
    }
    if (nextPage) {
        nextPage.addEventListener('click', function next() {
            if (viewer.currentPageNumber < viewer.pdfDocument.numPages) {
                viewer.currentPageNumber++;
                currentPageField.value = viewer.currentPageNumber;
            }
        });
    }
    if (currentPageField) {
        currentPageField.addEventListener('change', function changePage(e) {
            const pageRequested = parseInteger(e.target.value);
            if (pageRequested >= 1 && pageRequested <= viewer.pdfDocument.numPages) {
                viewer.currentPageNumber = pageRequested;
                currentPageField.value = viewer.currentPageNumber;
            }
            e.preventDefault();
        });
    }
}

/**
 * Retourne une instance de viewer PDF.
 *
 * @param {Element} container - Le conteneur HTML pour le viewer.
 * @param {Object} eventBus - Le bus d'événements pour la communication.
 * @param {Object} pdfLinkService - Service de gestion des liens PDF.
 * @return {PDFSinglePageViewer|PDFViewer} Une instance de PDFSinglePageViewer ou PDFViewer selon les attributs du conteneur.
 */
function getViewer(container, eventBus, pdfLinkService) {
    let viewer;
    const containerElement = container.querySelector('.js-pdf-viewer__content');

    // Vérifier si l'élément conteneur existe
    if (!containerElement) {
        return null;
    }
    containerElement.innerHTML = '';
    containerElement.style.position = 'absolute';

    const viewerElement = document.createElement('div');
    viewerElement.classList.add('pdfViewer');
    containerElement.appendChild(viewerElement);

    const options = {
        container: containerElement,
        eventBus,
        linkService: pdfLinkService,
        removePageBorders: false,
        textLayerMode: 1,
        renderer: 'canvas'
    };

    if (container.hasAttribute('data-pageByPage')) {
        viewer = new PDFSinglePageViewer(options);
    } else {
        viewer = new PDFViewer(options);
    }
    return viewer;
}

/**
 * Initialise pdf.js en fonction des paramètres fournis au tag.
 * @param {Element} container le conteneur du pdf viewer courant
 * @return {void}
 */
function initViewer(container) {
    const eventBus = new EventBus();
    const pdfLinkService = new PDFLinkService({eventBus});
    const currentPageField = container.querySelector('.js-currentPageField');
    const totalPage = container.querySelector('.js-totalPage');
    const viewer = getViewer(container, eventBus, pdfLinkService);
    if (!viewer) {
        return;
    }
    pdfLinkService.setViewer(viewer);
    initContainerListener(container, viewer, eventBus, currentPageField);
    window.addEventListener('resize', function windowResize() {
        viewer.currentScaleValue = 'auto';
    });
    getDocument(container.dataset.pdfurl).promise.then(function renderDoc(doc) {
        viewer.setDocument(doc);
        pdfLinkService.setDocument(doc);
        if (currentPageField) {
            currentPageField.setAttribute('max', doc.numPages);
        }
        if (totalPage) {
            totalPage.textContent = doc.numPages;
        }
    }).then(function hideWaiter() {
        const containerElement = container.querySelector('.js-pdf-viewer__content');
        containerElement.style.position = 'relative';
        const waiter = document.querySelector(`#waiter-${container.id}`);
        if (waiter) {
            waiter.classList.add('hide');
        }
    }).then(function notitfy() {
        window.dispatchEvent(new CustomEvent('pdfviewer:loaded', {detail: {viewer, eventBus}}));
    }).catch(function errorState(error) {
        // eslint-disable-next-line no-console
        console.error('impossible de rendre le PDF', error);
        const waiter = document.querySelector(`#waiter-${container.id}`);
        if (waiter) {
            waiter.classList.add('hide');
        }
    });
    container.classList.remove('js-initialize');
}

/**
 * Va chercher l'ensemble des viewers non initialisés et visible de la page pour lancer pdf.js
 * @return {void}
 */
function init() {
    [].forEach.call(document.querySelectorAll('[data-pdfurl].js-initialize'),
        function needToRender(element) {
            if (element.offsetParent !== null) {
                initViewer(element);
            }
        });
}

[].forEach.call(document.querySelectorAll('[data-pdfurl].js-initialize'), function needToRender(element) {
    if (element.offsetParent === null) {
        const lastUnvisibleElement = closest(element, (elem) => elem.offsetParent === null && elem.parentNode.offsetParent !== null);
        const mutationObserver = new MutationObserver(function onDomChange() {
            if (element.classList.contains('js-initialize') && element.offsetParent !== null) {
                initViewer(element);
                mutationObserver.disconnect();
            }
        });
        mutationObserver.observe(lastUnvisibleElement, {
            attributes: true,
            childList: true,
            attributeFilter: ['class', 'style']
        });
    } else {
        initViewer(element);
    }
});
observeOnDomChange(init);
